import React, { useState } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { ArrowRight, ArrowLeft, Clock, Target, Zap, TrendingUp } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { CategoryConfig, QuestionnaireData } from '@/types/category';
import { useAuth } from '@/contexts/AuthContext';

interface PersonalizationData {
  preferredTiming: string;
  stressTriggers: string[];
  meditationExperience: string;
  successMetric: string;
  sessionLength: string;
  timeOfDay: string;
}

interface PersonalizationFlowProps {
  category?: CategoryConfig;
  onComplete?: (data: QuestionnaireData) => Promise<void>;
  onBack?: () => void;
}

const PersonalizationFlow: React.FC<PersonalizationFlowProps> = ({ 
  category, 
  onComplete, 
  onBack 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { painPoint } = location.state || {};
  
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<PersonalizationData>({
    preferredTiming: '',
    stressTriggers: [],
    meditationExperience: '',
    successMetric: '',
    sessionLength: '',
    timeOfDay: ''
  });

  const getPainSpecificQuestions = (painPoint: string) => {
    const questionSets = {
      'racing_thoughts': {
        triggers: ['Work deadlines', 'Social situations', 'Before sleep', 'Financial stress', 'Health concerns'],
        metrics: ['Fall asleep faster', 'Focus during work', 'Reduce overthinking', 'Feel more calm'],
        timings: ['When thoughts spiral', 'Before bed', 'During work breaks', 'First thing morning']
      },
      'sleep_issues': {
        triggers: ['Caffeine late in day', 'Screen time before bed', 'Worrying about tomorrow', 'Noisy environment', 'Uncomfortable bed'],
        metrics: ['Fall asleep under 15 min', 'Sleep through the night', 'Wake up refreshed', 'Reduce sleep anxiety'],
        timings: ['30 min before bed', '1 hour before bed', 'When I wake up at night', 'Right when I get in bed']
      },
      'work_stress': {
        triggers: ['Heavy workload', 'Difficult colleagues', 'Tight deadlines', 'Meetings', 'Email overload'],
        metrics: ['Stay calm under pressure', 'Focus better at work', 'Leave work at work', 'Reduce physical tension'],
        timings: ['Before work starts', 'During lunch break', 'After work ends', 'Between meetings']
      },
      'general_anxiety': {
        triggers: ['Social events', 'Making decisions', 'Unexpected changes', 'Health concerns', 'Future planning'],
        metrics: ['Feel more confident', 'Worry less daily', 'Handle surprises better', 'Reduce physical symptoms'],
        timings: ['When anxiety hits', 'Morning routine', 'Before stressful events', 'End of day wind-down']
      }
    };
    
    return questionSets[painPoint as keyof typeof questionSets] || questionSets['general_anxiety'];
  };

  const questions = getPainSpecificQuestions(painPoint);

  const handleTriggerToggle = (trigger: string) => {
    setFormData(prev => ({
      ...prev,
      stressTriggers: prev.stressTriggers.includes(trigger)
        ? prev.stressTriggers.filter(t => t !== trigger)
        : [...prev.stressTriggers, trigger]
    }));
  };

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    } else {
      // If we have onComplete prop (from CategoryPageStates), use it
      if (onComplete) {
        // Convert PersonalizationData to QuestionnaireData format
        const questionnaireData: QuestionnaireData = {
          firstName: user?.user_metadata?.full_name?.split(' ')[0] || user?.email || '',
          preferredStyle: formData.meditationExperience,
          voiceType: 'Gentle calm voice',
          voiceGender: 'female',
          specific_voice_id: '', // Add the new field
          primaryGoal: formData.successMetric,
          triggers: formData.stressTriggers.join(', '),
          desiredFeeling: 'calm and centered',
          sessionLength: formData.sessionLength,
          backgroundSounds: true,
          backgroundMusicSelection: 'universal_background.mp3', // Default background music
          additionalNotes: `Preferred timing: ${formData.preferredTiming}`,
          environment: '' // Will be filled later in the flow
        };
        onComplete(questionnaireData);
      } else {
        // Original flow - store data and navigate
        localStorage.setItem('personalization_data', JSON.stringify(formData));
        navigate('/onboarding', { 
          state: {
            painPoint, 
            urgency: location.state?.urgency,
            personalizationComplete: true,
            formData 
          } 
        });
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      // If we have onBack prop, use it, otherwise use default navigation
      if (onBack) {
        onBack();
      }
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return formData.meditationExperience !== '';
      case 2: return formData.stressTriggers.length > 0;
      case 3: return formData.successMetric !== '';
      case 4: return formData.preferredTiming !== '' && formData.sessionLength !== '';
      default: return false;
    }
  };

  if (currentStep === 1) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-12">
        <div className="text-center mb-8">
          <Target className="h-12 w-12 text-purple-600 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            What's your meditation experience?
          </h2>
          <p className="text-lg text-gray-600">
            This helps us calibrate the right approach for you
          </p>
        </div>

        <Card>
          <CardContent className="p-8">
            <RadioGroup 
              value={formData.meditationExperience} 
              onValueChange={(value) => setFormData(prev => ({ ...prev, meditationExperience: value }))}
            >
              <div className="space-y-4">
                <div className="flex items-center space-x-3 p-4 rounded-lg hover:bg-gray-50">
                  <RadioGroupItem value="beginner" id="beginner" />
                  <Label htmlFor="beginner" className="text-lg cursor-pointer flex-1">
                    <strong>Complete beginner</strong> - Never tried meditation before
                  </Label>
                </div>
                <div className="flex items-center space-x-3 p-4 rounded-lg hover:bg-gray-50">
                  <RadioGroupItem value="some_experience" id="some_experience" />
                  <Label htmlFor="some_experience" className="text-lg cursor-pointer flex-1">
                    <strong>Some experience</strong> - Tried apps or classes occasionally
                  </Label>
                </div>
                <div className="flex items-center space-x-3 p-4 rounded-lg hover:bg-gray-50">
                  <RadioGroupItem value="regular_practice" id="regular_practice" />
                  <Label htmlFor="regular_practice" className="text-lg cursor-pointer flex-1">
                    <strong>Regular practice</strong> - Meditate weekly or more
                  </Label>
                </div>
                <div className="flex items-center space-x-3 p-4 rounded-lg hover:bg-gray-50">
                  <RadioGroupItem value="experienced" id="experienced" />
                  <Label htmlFor="experienced" className="text-lg cursor-pointer flex-1">
                    <strong>Very experienced</strong> - Daily practice for months/years
                  </Label>
                </div>
              </div>
            </RadioGroup>

            <div className="mt-8">
              <Button 
                onClick={handleNext}
                disabled={!canProceed()}
                className="w-full"
              >
                Continue <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (currentStep === 2) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-12">
        <div className="text-center mb-8">
          <Zap className="h-12 w-12 text-red-600 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            What usually triggers your stress?
          </h2>
          <p className="text-lg text-gray-600">
            Select all that apply - this helps us time your sessions perfectly
          </p>
        </div>

        <Card>
          <CardContent className="p-8">
            <div className="grid grid-cols-1 gap-3">
              {questions.triggers.map((trigger) => (
                <div 
                  key={trigger}
                  onClick={() => handleTriggerToggle(trigger)}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    formData.stressTriggers.includes(trigger)
                      ? 'border-purple-500 bg-purple-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="text-lg">{trigger}</span>
                </div>
              ))}
            </div>

            <div className="mt-8 flex space-x-4">
              <Button 
                variant="outline" 
                onClick={handleBack}
                className="flex-1"
              >
                <ArrowLeft className="h-4 w-4 mr-2" /> Back
              </Button>
              <Button 
                onClick={handleNext}
                disabled={!canProceed()}
                className="flex-1"
              >
                Continue <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (currentStep === 3) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-12">
        <div className="text-center mb-8">
          <TrendingUp className="h-12 w-12 text-green-600 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            How will you know this is working?
          </h2>
          <p className="text-lg text-gray-600">
            Pick your most important success metric
          </p>
        </div>

        <Card>
          <CardContent className="p-8">
            <RadioGroup 
              value={formData.successMetric} 
              onValueChange={(value) => setFormData(prev => ({ ...prev, successMetric: value }))}
            >
              <div className="space-y-4">
                {questions.metrics.map((metric) => (
                  <div key={metric} className="flex items-center space-x-3 p-4 rounded-lg hover:bg-gray-50">
                    <RadioGroupItem value={metric} id={metric} />
                    <Label htmlFor={metric} className="text-lg cursor-pointer flex-1">
                      {metric}
                    </Label>
                  </div>
                ))}
              </div>
            </RadioGroup>

            <div className="mt-8 flex space-x-4">
              <Button 
                variant="outline" 
                onClick={handleBack}
                className="flex-1"
              >
                <ArrowLeft className="h-4 w-4 mr-2" /> Back
              </Button>
              <Button 
                onClick={handleNext}
                disabled={!canProceed()}
                className="flex-1"
              >
                Continue <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-6 py-12">
      <div className="text-center mb-8">
        <Clock className="h-12 w-12 text-blue-600 mx-auto mb-4" />
        <h2 className="text-3xl font-bold text-gray-800 mb-4">
          When do you want to meditate?
        </h2>
        <p className="text-lg text-gray-600">
          Let's set up your ideal session schedule
        </p>
      </div>

      <Card>
        <CardContent className="p-8">
          <div className="space-y-8">
            <div>
              <Label className="text-lg font-semibold mb-4 block">Preferred timing:</Label>
              <RadioGroup 
                value={formData.preferredTiming} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, preferredTiming: value }))}
              >
                {questions.timings.map((timing) => (
                  <div key={timing} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50">
                    <RadioGroupItem value={timing} id={timing} />
                    <Label htmlFor={timing} className="cursor-pointer flex-1">
                      {timing}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            <div>
              <Label className="text-lg font-semibold mb-4 block">Session length:</Label>
              <RadioGroup 
                value={formData.sessionLength} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, sessionLength: value }))}
              >
                <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50">
                  <RadioGroupItem value="3 minutes" id="3min" />
                  <Label htmlFor="3min" className="cursor-pointer flex-1">
                    3 minutes - Quick relief when I need it
                  </Label>
                </div>
                <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50">
                  <RadioGroupItem value="5 minutes" id="5min" />
                  <Label htmlFor="5min" className="cursor-pointer flex-1">
                    5 minutes - Solid daily practice
                  </Label>
                </div>
                <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50">
                  <RadioGroupItem value="7 minutes" id="7min" />
                  <Label htmlFor="7min" className="cursor-pointer flex-1">
                    7 minutes - Deep relaxation sessions
                  </Label>
                </div>
              </RadioGroup>
            </div>
          </div>

          <div className="mt-8 flex space-x-4">
            <Button 
              variant="outline" 
              onClick={handleBack}
              className="flex-1"
            >
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
            <Button 
              onClick={handleNext}
              disabled={!canProceed()}
              className="flex-1"
            >
              Complete Setup <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PersonalizationFlow;
